import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { useGetFinancialCovenantCalculationsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  CurrencyLimitGraph,
  PercentageLimitGraph,
  RatioLimitGraph,
} from './FinancialCovenantChart';
import { InsightsHighlightBox } from './InsightsTemplate';
import {
  getFormattedFinancialCovenantCalculations,
  parsePortfolioCovenantsFromAllCovenants,
} from './utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';

const startDate = commonDate.currentDateTime().minus({ months: 6 }).toMillis();
const endDate = commonDate.currentEpochMs();

export const PortfolioCovenantsContainer: React.FC<{}> = () => {
  const { creditFacilityId } = useCreditFacilityContext();
  const {
    data: covenantData,
    loading: covenantLoading,
  } = useGetFinancialCovenantCalculationsQuery({
    variables: {
      creditFacilityId,
      startDate,
      endDate,
    },
  });

  if (
    covenantLoading ||
    !covenantData ||
    !covenantData.financialCovenantCalculations
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-covenants-route-loading"
      />
    );
  }
  const insights = parsePortfolioCovenantsFromAllCovenants({
    allCovenantData: covenantData,
  });

  if (insights.config.length === 0) {
    return (
      <Alert status="info" mt="20px">
        <AlertIcon />
        <AlertDescription>No portfolio covenants configured</AlertDescription>
      </Alert>
    );
  }

  const formattedCalculations = getFormattedFinancialCovenantCalculations(
    insights,
  );

  return (
    <>
      <SimpleGrid
        columns={{
          sm: 1,
          md: 2,
          lg: 2,
          xl: 2,
        }}
        spacingX={8}
        spacingY={10}
        mb={30}
      >
        {_.map(insights.config, (config) => {
          const covenantKey = config.key as CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey;
          const recordCount = formattedCalculations[covenantKey].length;
          if (recordCount === 0) {
            return (
              <InsightsHighlightBox key={config.key}>
                <Box>
                  <Heading as="h3" fontSize="xl" mb="30px">
                    {commonUtils.toTitleCase(covenantKey)}
                  </Heading>
                  <Alert status="info" mt="20px">
                    <AlertIcon />
                    <AlertDescription>
                      No covenant data available
                    </AlertDescription>
                  </Alert>
                </Box>
              </InsightsHighlightBox>
            );
          }

          if (config.valueType === 'currency') {
            return (
              <InsightsHighlightBox key={config.key}>
                <Box>
                  <CurrencyLimitGraph
                    covenantKey={covenantKey}
                    data={formattedCalculations[covenantKey]}
                  />
                </Box>
              </InsightsHighlightBox>
            );
          }
          if (config.valueType === 'ratio') {
            return (
              <InsightsHighlightBox key={config.key}>
                <Box>
                  <RatioLimitGraph
                    covenantKey={covenantKey}
                    data={formattedCalculations[covenantKey]}
                  />
                </Box>
              </InsightsHighlightBox>
            );
          }
          if (config.valueType === 'percentage') {
            return (
              <InsightsHighlightBox key={config.key}>
                <Box>
                  <PercentageLimitGraph
                    covenantKey={covenantKey}
                    data={formattedCalculations[covenantKey]}
                  />
                </Box>
              </InsightsHighlightBox>
            );
          }
          return null;
        })}
      </SimpleGrid>
    </>
  );
};
